import {CartItem} from './cart-item.model';
import {BillingAddress, GroupOrderRequest, LineItem, Order} from './payment-item.model';
import {Price} from './restaurant.model';
import { Modifier } from './catalog.model';

export enum FulfillmentType  {
  PICKUP = 'pickup',
  CURBSIDE = 'curbside',
  TABLESIDE = 'tableside',
  DELIVERY = 'delivery',
  HANDOFF = 'handoff',
  TAB = 'tab',
  GIFTCARD = 'giftcard',
  FRIDGE = 'fridge'
}

export enum TipType  {
  FLAT = 'FLAT',
  PERCENTAGE = 'PERCENTAGE',
  SMART = 'SMART'
}

export enum DeliveryType {
  SELLER = 'seller',
  COURIER = 'courier'
}

export const StatusColors: {} = {
  'PROPOSED': 'red',
  'RESERVED': 'yellow',
  'PREPARED': 'green',
  'COMPLETED': 'blue'
};

export const StatusesFromServer: string[] = [
  'PROPOSED',
  'RESERVED',
  'PREPARED',
  'COMPLETED',
];

export const StatusesToOrderView: string[] = [
  'NEW',
  'ACCEPTED',
  'READY',
  'PICKED UP',
  'ARCHIVED'
];

export const StatusesToButtonView: string[] = [
  'ACCEPT',
  'MARK READY',
  'MARK PICKED UP',
  'ARCHIVE'
];

export const PastOrderStatuses: string[] = ['READY', 'COMPLETED'];

export interface UpdatedOrderInfo {
  idForUpdatedOrder: string;
  updatedStatus: string;
}

export class OrderData {
  constructor(
    public orderId: string,
    public order: Order,
    public locationId: string
  ) {
  }
}

export class OrderStatus {
  constructor(
    public orderId: string = null,
    public number: string = null,
    public status: string = null,
    public customerId: string = null,
    public datetime_created?: string,
    public fulfillmentType?: string,
    public isAsap?: boolean,
    public pickupAt?: string,
    public lineItems: CartItem[] = [],
    public locationId: string = null,
    public locationName: string = null,
    public squareReceiptUrl: string = null,
    public squareSourceId: string = null,
    public prepTimeDuration: string = null,
    public transactionId: string = null
  ) {
  }

  public get formattedStatus(): string {
    switch (this.status) {
      case 'PROPOSED': return 'NEW';
      case 'RESERVED': return 'IN PROGRESS';
      case 'PREPARED': return 'READY';
      case 'COMPLETED': return 'COMPLETE';
      default: return this.status;
    }
  }

  public get hasPastStatus(): boolean {
    return PastOrderStatuses.includes(this.status);
  }
}

export class TabOrderStatus {
  constructor(
    public orderId: string,
    public number: string,
    public customerId: string,
    public lineItems?: LineItem[]
  ) {
  }
}

export class SubscriptionOrderStatus {
  constructor(
    public id: string,
    public cadence: string,
    public dayTypes: string[],
    public periods: string,
    public lineItems?: CartItem[]
  ) {
  }
}

export class DeliveryAddress {
  constructor(
    public street: string,
    public suite: string,
    public city: string,
    public state: string,
    public postalCode: string,
    public instructions: string
  ) {
  }
}

export class Address {
  constructor(
    public address: string = null,
    public suite: string = null,
    public city: string = null,
    public state: string = null,
    public postal_code: string = null,
    public delivery_instructions: string = null
  ) {
  }
}

export class DeliveryPreEstimateRequest {
  constructor(
    public delivery_address: Address = new Address(),
    public location_ids?: string[]
  ) {
  }
}

export class DeliveryPreEstimateResponseData {
  constructor(
    public duration: string = null,
    public pickup_duration: string = null
  ) {
  }
}

export class DeliveryRequestData {
  constructor(
    public delivery_address: Address,
    public customer_phone_number: string,
    public dropoff_at?: string,
    public subtotal?: number,
    public location_ids?: string[]
) {
  }
}

export class DeliveryResponseData {
  constructor(
    public quote_id: string,
    public duration: string,
    public delivery_fee_cents: number,
    public kyoo_fee_cents: number,
    public app_fee_cents: number,
    public pickup_duration: string,
    public expires: string,
    public delivery_type: string
  ) {
  }
}

export class CreateSubscriptionData {
  constructor(
    public subscription_plan_id: string,
    public cadence: string,
    public day_types: (string | number)[],
    public location_id: string,
    public indexes: (string | number)[],
    public card_id?: string,
    public order_request?: Order,
    public subscription_id?: string
  ) {
  }
}

export class CreateMembershipData {
  constructor(
    public plan_id:string,
    public customer_id: string,
    public card_id: string,
    public order_id: string,
    public location_id: string,
    public transaction_id: string,
    public invoice_amount: number,
    public product_name: string,
    public buyer_email: string
  ){}
}

export class SubscriptionPhase {
  constructor(
    public subscriptionId: string,
    public subscriptionName: string,
    public uid: string,
    public ordinal: string,
    public cadence: string,
    public periods: string,
    public price: Price,
    public eligibleItemIds: string[],
    public phaseId: string,
    public pricing: any,
    public selected = false,
    public selectedPeriods: SelectDataState[] = [{selected: true, param: '1st', value: 1}, {selected: false, param: '2nd', value: 2}, {selected: false, param: '3rd', value: 3}, {selected: false, param: 'last', value: 4}],
  ) {
  }
}

export class SelectDataState {
  constructor(
    public selected: boolean,
    public param: string,
    public value: string | number,
) {
  }
}

export interface QueueInfo {
  placeInLine: number;
  estimatedTimeToBeginPreparation: string;
  numPersonsAhead: number;
  maxOrdersInQueue: number;
}

export class TimeInterval {
  constructor(
    public start: Date = null,
    public end: Date = null
  ) {
  }

}

export class DateTime {
  constructor(
    public date: Date = null,
    public timeInterval: TimeInterval = null) {}
}
export class DatePickUpTime {
  constructor(
    public dateTime: DateTime = new DateTime(),
    public asap: boolean = false,
    public fullDayPickUpAvailable: boolean = false
  ) {
  }
}

export interface OrderDetails {
  tipMoney: number;
  delivery_gratuity_cents: number;
  pickupAt?: string;
  pickupLocation?: string,
  deliveryAt?: string;
  deliveryQuoteId?: string;
  printAheadDuration?: string;
  pickupWindowDuration?: string;
  fulfillmentType: FulfillmentType;
  givenName: string;
  familyName: string;
  email?: string;
  billingAddress?: BillingAddress;
  deliveryAddress?: Address;
  saveDeliveryAddress?: boolean;
  tableNumber?: string;
  isTabOrder?: boolean;
  isGroupOrder?: boolean;
  isAsap?: boolean;
  useLoyaltyPoints?: boolean;
  createOrderRequest?: GroupOrderRequest[];
  platformName?: string;
}

export interface PhoneFormat {
  countryCode: string;
  dialCode: string;
  e164Number: string;
  internationalNumber: string;
  nationalNumber: string;
  number: string;
}

export class EstimateOrder {
  constructor(
    public catalog_object_id: string = null,
    public category_id: string = null,
    public quantity: string = '1',
    public duration: string = null
  ) {
  }
}

export class Money {
  constructor(
    public amount: number = 0,
    public currency: string = 'USD'
  ) {}
}

export class LineItemUser {
  constructor(
    public name: string = null,
    public variation_name: string = null,
    public quantity: string = null,
    public modifiers: Modifier[] = [],
    public total_price_money: Money = new Money(),
    public order_number: string = null,
    public location_id: string = null
  ) {}
}

export class OrderStatusUser {
  constructor(
    public order_number: string = null,
    public status: string = null,
    public datetime_created?: string,
    public fulfillment_type?: string,
    public pickup_at?: string,
    public prep_time_duration?: string,
    public line_items: LineItemUser[] = [],
    public discount_money: Money = new Money(),
    public tax_money: Money = new Money(),
    public delivery_fee_money: Money = new Money(),
    public tip_money: Money = new Money(),
    public driver_tip_money: Money = new Money(),
    public total_money: Money = new Money(),
    public is_asap?: boolean,
    public transaction_id: string = null,
    public table_number: string = null,
  ) {}

  public get formattedStatus(): string {
    switch (this.status) {
      case 'PROPOSED': return 'NEW';
      case 'RESERVED': return 'IN PROGRESS';
      case 'PREPARED': return 'READY';
      case 'COMPLETED': return 'COMPLETE';
      default: return this.status;
    }
  }
}

export class Membership {
  constructor(
    public subscriptionId: string,
    public planId: string,
    public customerId: string,
    public status: string,
    public startDate: string,
    public canceledDate: string,
    public chargedThroughDate: string,
    public actions: MembershipActions[]
  ){}
}

export class MembershipActions {
  constructor(
    public type:string,
    public effective_date:string,
  ){}
}

export class AtriumOrder {
 constructor(
   public transaction_id: string = null,
   public token: string = null,
   public source_id: string = null
 ) {
 }
}
